import { Card, CardContent, CardHeader, Box, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatValue, pickPointColor, type BaseChartConfig, type Widget } from "./types";

const AXIS_COLOR = "#374151";   // gray-700, readable on any light bg
const PALETTE_FALLBACK = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
const GRID_COLOR = "rgba(0,0,0,0.08)";
const TOOLTIP_BG = "rgba(15, 23, 42, 0.92)"; // slate-900 @ 92%
const TOOLTIP_TEXT = "#f9fafb";

type Point = { label: string; value: number } & Record<string, unknown>;

export function BarsCard({ widget }: { widget: Widget }) {
  const cfg = widget.config as BaseChartConfig;
  const horizontal = widget.kind === "hbars";
  const rawPoints = widget.data?.points ?? [];
  const points: Point[] = rawPoints
    .filter((p) => p && typeof p === "object")
    .map((p) => {
      const o = p as Record<string, unknown>;
      const label = String(o.label ?? o.name ?? o.x ?? "");
      const value =
        typeof o.value === "number" ? o.value
          : typeof o.y === "number" ? o.y
          : Number(o.value ?? o.y ?? 0);
      return { ...o, label, value };
    });

  const fmt = cfg.format ?? "number";
  const tooltipFormatter = (v: number) => formatValue(v, fmt);
  const axisTickFormatter = (v: number) => formatValue(v, fmt);

  return (
    <Card variant="outlined">
      {widget.title && (
        <CardHeader
          title={widget.title}
          titleTypographyProps={{ variant: "subtitle1", fontWeight: 700, letterSpacing: "-0.01em" }}
          subheaderTypographyProps={{ variant: "caption", color: "text.secondary" }}
          sx={{ pb: 1 }}
        />
      )}
      <CardContent sx={{ pt: 0 }}>
        {points.length === 0 ? (
          <EmptyState error={widget.data?.error} />
        ) : (
          <Box sx={{ width: "100%", height: { xs: 320, sm: 380, md: 420 } }}>
            <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={points}
              layout={horizontal ? "vertical" : "horizontal"}
              margin={
                horizontal
                  ? { top: 8, right: 24, left: 8, bottom: 24 }
                  : { top: 8, right: 24, left: 24, bottom: 56 }
              }
            >
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
              <XAxis
                {...(horizontal
                  ? { type: "number" as const, tickFormatter: axisTickFormatter }
                  : {
                      dataKey: "label",
                      interval: 0 as const,
                      angle: -20,
                      textAnchor: "end" as const,
                      height: 56,
                    })}
                tick={{ fill: AXIS_COLOR, fontSize: 11 }}
                stroke={AXIS_COLOR}
                label={
                  !horizontal && cfg.x_label
                    ? {
                        value: cfg.x_label,
                        position: "insideBottom",
                        offset: -16,
                        fill: AXIS_COLOR,
                      }
                    : horizontal && cfg.x_label
                      ? {
                          value: cfg.x_label,
                          position: "insideBottom",
                          offset: -4,
                          fill: AXIS_COLOR,
                        }
                      : undefined
                }
              />
              <YAxis
                {...(horizontal
                  ? {
                      type: "category" as const,
                      dataKey: "label",
                      width: 160,
                    }
                  : { tickFormatter: axisTickFormatter })}
                tick={{ fill: AXIS_COLOR, fontSize: 11 }}
                stroke={AXIS_COLOR}
                label={
                  !horizontal && cfg.y_label
                    ? {
                        value: cfg.y_label,
                        angle: -90,
                        position: "insideLeft",
                        offset: 0,
                        style: { textAnchor: "middle" },
                        fill: AXIS_COLOR,
                      }
                    : undefined
                }
                width={56}
              />

              <Tooltip
                contentStyle={{
                  backgroundColor: TOOLTIP_BG,
                  border: "none",
                  borderRadius: 6,
                  color: TOOLTIP_TEXT,
                }}
                labelStyle={{ color: TOOLTIP_TEXT, fontWeight: 600 }}
                itemStyle={{ color: TOOLTIP_TEXT }}
                formatter={(v: number) => [tooltipFormatter(v), cfg.y_label ?? "Value"]}
                cursor={{ fill: "rgba(0,0,0,0.04)" }}
              />
              {cfg.target_line !== undefined && (
                <ReferenceLine
                  y={horizontal ? undefined : cfg.target_line}
                  x={horizontal ? cfg.target_line : undefined}
                  stroke="#ef4444"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  label={{ value: `Target ${formatValue(cfg.target_line, fmt)}`, fill: "#ef4444", fontSize: 11, position: horizontal ? "insideTopRight" : "insideTopRight" }}
                />
              )}
              {/* Stacked bars: when cfg.stacked is true OR cfg.series declares
                  multiple keys, render one <Bar> per series with the same
                  stackId. Single-series falls back to the per-bar Cell
                  coloring (rainbow / highlight). */}
              {(() => {
                const series = (cfg as { series?: { key: string; label?: string; color?: string }[] }).series;
                const stacked = (cfg as { stacked?: boolean }).stacked;
                if (series && series.length > 0) {
                  return series.map((s, i) => (
                    <Bar
                      key={s.key}
                      dataKey={s.key}
                      name={s.label ?? s.key}
                      stackId={stacked ? "stack" : undefined}
                      radius={
                        stacked
                          ? undefined
                          : horizontal
                            ? [0, 4, 4, 0]
                            : [4, 4, 0, 0]
                      }
                      fill={s.color ?? PALETTE_FALLBACK[i % PALETTE_FALLBACK.length]}
                    />
                  ));
                }
                return (
                  <Bar
                    dataKey="value"
                    radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
                  >
                    {points.map((_, j) => (
                      <Cell key={j} fill={pickPointColor(j, points.length, cfg)} />
                    ))}
                  </Bar>
                );
              })()}
              {(() => {
                const series = (cfg as { series?: unknown[] }).series;
                if (series && series.length > 1) return <Legend wrapperStyle={{ fontSize: 12 }} />;
                return null;
              })()}
            </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyState({ error }: { error?: string }) {
  return (
    <Box sx={{ py: 4, textAlign: "center" }}>
      <Typography variant="body2" color="text.secondary">
        {error ? `No data (${error})` : "No data."}
      </Typography>
    </Box>
  );
}
